// src/lib/firebase.ts
import { initializeApp, getApps, getApp } from 'firebase/app';
import { 
  getAuth, 
  connectAuthEmulator,
  signInWithEmailAndPassword, 
  signInWithPopup,
  GithubAuthProvider,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  User,
  Auth,
  UserCredential
} from 'firebase/auth';
import { 
  getFirestore, 
  connectFirestoreEmulator,
  doc, 
  getDoc, 
  setDoc, 
  addDoc,
  deleteDoc,
  updateDoc,
  writeBatch,
  collection,
  query,
  where,
  getDocs,
  increment,
  serverTimestamp,
  orderBy,
  onSnapshot,
  limit,
  Firestore,
  CollectionReference
} from 'firebase/firestore';
import type { Document, ChatMessage } from '@/types';
import { predefinedGroups } from '@/lib/groups';

// Define all interfaces at the top
export interface Template {
  templateId: string;
  name: string;
  avatar: string;
  defaultMessage: string;
  defaultInstructions: string;
  createdAt: number;
  updatedAt: number;
  isTemplate: boolean;
  tools?: string[];
  toolConfigs?: Record<string, Record<string, string | number | boolean | string[]>>;
  createdBy?: string; // User ID for custom templates
  tags?: string[]; // Tags for categorization and access control
}

/**
 * Assistant interface for Firestore documents.
 * All timestamp fields (createdAt, updatedAt, lastAccessedAt) must be numbers (milliseconds since epoch),
 * consistent with the Firestore rules validation.
 */
export interface Assistant {
  assistantId: string;
  templateId: string;
  name: string;
  avatar: string;
  ownerId: string;
  ownerEmail: string;
  initialMessage: string;   // First greeting message - can be AI-generated or copied from parent
  initialInstructions: string;
  initialDocuments: Document[];
  tools: string[];
  toolConfigs: Record<string, Record<string, string | number | boolean | string[]>>;
  createdAt: number;        // Milliseconds since epoch (numeric timestamp)
  updatedAt: number;        // Milliseconds since epoch (numeric timestamp)
  usageCount: number;
  lastAccessedAt: number;   // Milliseconds since epoch (numeric timestamp)
  accessControl: AccessControl;
  tags?: string[]; // Tags for categorization and access control
  emailSubscriptions?: string[]; // List of email addresses subscribed to notifications
  // Provenance tracking
  parentAssistantId?: string;           // Direct parent for quick navigation
  derivationType?: 'template' | 'duplicate'; // How this assistant was created
  generationNumber?: number;            // Depth in conversation tree (0=fresh, 1=first copy, etc.)
}

export interface AccessControl {
  type: 'public' | 'private' | 'domain' | 'domains' | 'specific' | 'group';
  domain?: string;
  domains?: string[];
  emails?: string[];
  groups?: string[];
}

/**
 * Tag interface for managing tags and their access controls
 */
export interface Tag {
  id: string;
  name: string;
  description?: string;
  accessControl: AccessControl;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
}

export interface Message {
  id?: string;
  content: string;
  userEmail: string;
  userName: string;
  sender: 'user' | 'assistant' | 'system';
  timestamp: number;
  read: boolean;
  photoURL?: string | null;
  thinkingContent?: string;
  artifacts?: import('@/types').ArtifactData[];
  traceId?: string;
  structuredOutput?: any; // Structured extraction results from backend
}

export interface UserProfile {
  userId: string;
  email: string;
  displayName: string;
  photoURL?: string;
  createdAt: number;
  lastLogin: number;
  assistantCount: number;
}

// Firebase configuration
const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID
};

// Debug logging
//console.log('Firebase Config Environment Variables:', {
//  apiKey: firebaseConfig.apiKey ? 'Set' : 'Not Set',
//  authDomain: firebaseConfig.authDomain ? 'Set' : 'Not Set',
//  projectId: firebaseConfig.projectId ? 'Set' : 'Not Set',
//  storageBucket: firebaseConfig.storageBucket ? 'Set' : 'Not Set',
//  messagingSenderId: firebaseConfig.messagingSenderId ? 'Set' : 'Not Set',
//  appId: firebaseConfig.appId ? 'Set' : 'Not Set'
//});

export function initFirebase() {
  let app;
  if (!getApps().length) {
    app = initializeApp(firebaseConfig);
    
    // Connect to emulators immediately after app initialization
    // but only in the browser
    if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_USE_FIREBASE_EMULATORS === 'true') {
      // Initialize Auth and connect to emulator
      const auth = getAuth(app);
      connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true });
      
      // Initialize Firestore and connect to emulator
      const db = getFirestore(app);
      connectFirestoreEmulator(db, '127.0.0.1', 8080);
    }
  } else {
    app = getApp();
  }

  return app;
}

class FirebaseService {
  private static app = typeof window !== 'undefined' ? initFirebase() : null;
  private static _auth: Auth | null = null;
  private static _db: Firestore | null = null;
  private static _googleProvider: GoogleAuthProvider | null = null;
  private static _githubProvider: GithubAuthProvider | null = null;

  private static initialize() {
    if (typeof window === 'undefined') {
      throw new Error('Firebase can only be initialized in the browser');
    }

    if (!this.app) {
      this.app = initFirebase();
    }

    if (!this._auth) {
      this._auth = getAuth(this.app);
    }

    if (!this._db) {
      this._db = getFirestore(this.app);
    }

    if (!this._googleProvider) {
      this._googleProvider = new GoogleAuthProvider();
    }

    if (!this._githubProvider) {
      this._githubProvider = new GithubAuthProvider();
    }
  }

  private static get db() {
    if (!this._db) {
      this.initialize();
    }
    return this._db!;
  }

  static get auth() {
    if (!this._auth) {
      this.initialize();
    }
    return this._auth!;
  }

  private static get googleProvider() {
    if (!this._googleProvider) {
      this.initialize();
    }
    return this._googleProvider!;
  }

  private static get githubProvider() {
    if (!this._githubProvider) {
      this.initialize();
    }
    return this._githubProvider!;
  }

  private static handleError(error: any, context: string) {
    console.error(`${context} Error:`, error);
    throw new Error(`${context} failed: ${error.message}`);
  }

  // Authentication Methods
  private static handleAuthError(error: any) {
    console.error('Auth Error:', error);
    
    const errorMessages: { [key: string]: string } = {
      'auth/invalid-email': 'Please enter a valid email address.',
      'auth/user-disabled': 'This account has been disabled.',
      'auth/user-not-found': 'No account found with this email.',
      'auth/wrong-password': 'Incorrect password.',
      'auth/popup-blocked': 'Please enable popups for this website.',
      'auth/popup-closed-by-user': 'Login was cancelled.',
      'auth/account-exists-with-different-credential': 
        'An account already exists with the same email address but different sign-in credentials.',
      'auth/network-request-failed': 'A network error occurred. Please try again.',
      'auth/too-many-requests': 'Too many unsuccessful login attempts. Please try again later.',
    };

    throw new Error(errorMessages[error.code] || 'An unexpected error occurred.');
  }

  static async loginWithEmail(email: string, password: string): Promise<UserCredential | null> {
    if (typeof window === 'undefined') return null;
    try {
      const credential = await signInWithEmailAndPassword(this.auth, email, password);
      await this.createOrUpdateUserProfile(credential.user);
      return credential;
    } catch (error: any) {
      this.handleAuthError(error);
      return null;
    }
  }

  static async loginWithGoogle(): Promise<UserCredential | null> {
    if (typeof window === 'undefined') return null;
    try {
      this.googleProvider.setCustomParameters({ prompt: 'select_account' });
      const credential = await signInWithPopup(this.auth, this.googleProvider);
      await this.createOrUpdateUserProfile(credential.user);
      return credential;
    } catch (error: any) {
      this.handleAuthError(error);
      return null;
    }
  }

  static async loginWithGithub(): Promise<UserCredential | null> {
    if (typeof window === 'undefined') return null;
    try {
      this.githubProvider.setCustomParameters({ prompt: 'select_account' });
      const credential = await signInWithPopup(this.auth, this.githubProvider);
      await this.createOrUpdateUserProfile(credential.user);
      return credential;
    } catch (error: any) {
      this.handleAuthError(error);
      return null;
    }
  }

  static async logout(): Promise<void> {
    if (typeof window === 'undefined') return;
    try {
      await signOut(this.auth);
    } catch (error: any) {
      this.handleAuthError(error);
    }
  }

  static onAuthStateChange(callback: (user: User | null) => void) {
    if (typeof window === 'undefined') return () => {};
    return onAuthStateChanged(this.auth, callback);
  }

  // User Profile Management
  private static async createOrUpdateUserProfile(user: User): Promise<void> {
    if (!user || !user.email) return;
    
    try {
      const userRef = doc(this.db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        // Update existing user
        await updateDoc(userRef, {
          email: user.email,
          displayName: user.displayName || 'User',
          photoURL: user.photoURL,
          lastLogin: Date.now()
        });
      } else {
        // Create new user
        await setDoc(userRef, {
          userId: user.uid,
          email: user.email,
          displayName: user.displayName || 'User',
          photoURL: user.photoURL,
          createdAt: Date.now(),
          lastLogin: Date.now(),
          assistantCount: 0
        });
      }
    } catch (error) {
      console.error('Error updating user profile:', error);
      // Don't throw, as we don't want auth to fail if profile update fails
    }
  }

  static async getUserProfile(userId: string): Promise<UserProfile | null> {
    try {
      const userRef = doc(this.db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        return userSnap.data() as UserProfile;
      }
      return null;
    } catch (error: any) {
      this.handleError(error, 'Getting user profile');
      throw error;
    }
  }
  
  // Template Management
  static async getTemplates(): Promise<Template[]> {
    try {
      const templatesRef = collection(this.db, 'templates');
      const q = query(templatesRef, where('isTemplate', '==', true));
      const templatesSnap = await getDocs(q);
      
      return templatesSnap.docs.map(doc => doc.data() as Template);
    } catch (error: any) {
      this.handleError(error, 'Getting templates');
      throw error;
    }
  }

  /**
   * Get templates filtered by user's tag access
   */
  static async getTemplatesWithTagAccess(
    availableTags: Tag[], 
    user: User | null
  ): Promise<Template[]> {
    try {
      const allTemplates = await this.getTemplates();
      
      // Import checkTagAccess here to avoid circular dependencies
      const { checkTagAccess } = await import('@/lib/access-control');
      
      return allTemplates.filter(template => {
        // If template has no tags, it's available to everyone
        if (!template.tags || template.tags.length === 0) {
          return true;
        }
        
        // Check if user has access to at least one of the template's tags
        return checkTagAccess(template.tags, availableTags, user);
      });
    } catch (error: any) {
      this.handleError(error, 'Getting templates with tag access');
      throw error;
    }
  }

  static async getTemplate(templateId: string): Promise<Template | null> {
    try {
      const templateRef = doc(this.db, 'templates', templateId);
      const templateSnap = await getDoc(templateRef);
      
      return templateSnap.exists() ? templateSnap.data() as Template : null;
    } catch (error: any) {
      this.handleError(error, 'Getting template');
      throw error;
    }
  }

  static async createTemplate(templateData: Omit<Template, 'templateId' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const user = this.auth.currentUser;
      if (!user) throw new Error('User not authenticated');
      
      const templateId = crypto.randomUUID();
      const timestamp = Date.now();
      
      const template: Template = {
        ...templateData,
        templateId,
        createdAt: timestamp,
        updatedAt: timestamp,
        isTemplate: false, // Custom templates aren't system templates
        createdBy: user.uid,
        tools: templateData.tools || [],
        toolConfigs: templateData.toolConfigs || {}
      };
      
      const templateRef = doc(this.db, 'templates', templateId);
      await setDoc(templateRef, template);
      
      return templateId;
    } catch (error: any) {
      this.handleError(error, 'Creating template');
      throw error;
    }
  }

  static async updateTemplate(templateId: string, updates: Partial<Template>): Promise<void> {
    try {
      const user = this.auth.currentUser;
      if (!user) throw new Error('User not authenticated');
      
      const templateRef = doc(this.db, 'templates', templateId);
      const templateSnap = await getDoc(templateRef);
      
      if (!templateSnap.exists()) {
        throw new Error('Template not found');
      }
      
      const template = templateSnap.data() as Template;
      
      // Only allow update if user is the creator or it's a system template
      if (template.createdBy && template.createdBy !== user.uid) {
        throw new Error('Not authorized to update this template');
      }
      
      await updateDoc(templateRef, {
        ...updates,
        updatedAt: Date.now()
      });
    } catch (error: any) {
      this.handleError(error, 'Updating template');
      throw error;
    }
  }
  
  // Assistant Management
  static async createAssistant(
    assistantData: Omit<Assistant, 'assistantId' | 'createdAt' | 'updatedAt' | 'usageCount' | 'lastAccessedAt'>,
    options?: {
      parentAssistantId?: string;
      copyFirstMessage?: boolean;
    }
  ): Promise<string> {
    try {
      const user = this.auth.currentUser;
      if (!user || !user.email) throw new Error('User not authenticated');

      const assistantId = crypto.randomUUID();
      const timestamp = Date.now();
      let template: Template | null = null; // Initialize template variable
      let parentChain: string[] = [];
      let parentGenerationNumber = 0;

      // Get parent assistant data if duplicating
      if (options?.parentAssistantId) {
        try {
          const parentAssistant = await this.getAssistant(options.parentAssistantId);
          if (parentAssistant) {
            parentGenerationNumber = parentAssistant.generationNumber || 0;
          }
        } catch (error) {
          console.warn('Could not fetch parent assistant data:', error);
        }
      }

      // Only fetch the template if the ID is not 'custom'
      if (assistantData.templateId && assistantData.templateId !== 'custom') {
        template = await this.getTemplate(assistantData.templateId);
        if (!template) {
          // Throw error only if a specific template ID was given but not found
          throw new Error(`Template ${assistantData.templateId} not found`);
        }
      }

      const defaultName = 'Custom Assistant';
      const defaultAvatar = '/images/avatars/aitana_help.jpeg';
      const defaultMessage = 'Hello! How can I assist you?';
      const defaultInstructions = 'You are a helpful assistant.';

      const assistant: Assistant = {
        ...assistantData,
        assistantId,
        name: assistantData.name || (template ? template.name : defaultName),
        avatar: assistantData.avatar || (template ? template.avatar : defaultAvatar),
        initialMessage: assistantData.initialMessage || (template ? template.defaultMessage : defaultMessage),
        initialInstructions: assistantData.initialInstructions || (template ? template.defaultInstructions : defaultInstructions),
        ownerId: user.uid,
        ownerEmail: user.email,
        createdAt: timestamp,
        updatedAt: timestamp,
        usageCount: 0,
        lastAccessedAt: timestamp,
        tools: assistantData.tools || (template ? template.tools : []) || [],
        toolConfigs: assistantData.toolConfigs || (template ? template.toolConfigs : {}) || {},
        initialDocuments: assistantData.initialDocuments || [],
        accessControl: assistantData.accessControl || { type: 'private' },
        emailSubscriptions: assistantData.emailSubscriptions || [],
        // Provenance tracking - only include parentAssistantId if it has a value
        ...(options?.parentAssistantId && {
          parentAssistantId: options.parentAssistantId,
          derivationType: 'duplicate' as const,
          generationNumber: parentGenerationNumber + 1
        }),
        // Always include derivationType and generationNumber for non-duplicates
        ...(!options?.parentAssistantId && {
          derivationType: 'template' as const,
          generationNumber: 0
        })
      };
      
      console.log(`Creating assistant with numeric timestamps: createdAt=${timestamp}, updatedAt=${timestamp}, lastAccessedAt=${timestamp}`);

      // Validate against Firestore rules before attempting to create
      const requiredFields = ['assistantId', 'templateId', 'name', 'avatar', 'ownerId', 'ownerEmail',
                             'initialMessage', 'initialInstructions', 'tools', 'toolConfigs',
                             'createdAt', 'updatedAt', 'usageCount', 'lastAccessedAt', 'accessControl'];
      const optionalFields = ['initialDocuments', 'tags', 'emailSubscriptions', 'parentAssistantId', 'derivationType', 'generationNumber', 'structuredInstructions', 'personaTemplateId'];
      const allowedFields = [...requiredFields, ...optionalFields];

      // Check for missing required fields
      const missingFields = requiredFields.filter(field => !(field in assistant));
      if (missingFields.length > 0) {
        console.error('❌ Missing required fields:', missingFields);
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }

      // Check for extra fields not allowed by Firestore rules
      const assistantFields = Object.keys(assistant);
      const extraFields = assistantFields.filter(field => !allowedFields.includes(field));
      if (extraFields.length > 0) {
        console.error('❌ Extra fields not allowed by Firestore rules:', extraFields);
        console.error('Allowed fields:', allowedFields);
        console.error('Assistant fields:', assistantFields);
        throw new Error(`Extra fields not allowed: ${extraFields.join(', ')}. Please remove these fields before creating assistant.`);
      }

      console.log('✅ Field validation passed:', {
        hasAllRequired: missingFields.length === 0,
        noExtraFields: extraFields.length === 0,
        totalFields: assistantFields.length
      });

      // Log the complete assistant object being sent to Firestore
      console.log('📝 Complete assistant object being sent to Firestore:');
      console.log(JSON.stringify(assistant, null, 2));

      // Check each field type
      console.log('🔍 Field type validation:');
      Object.keys(assistant).forEach(key => {
        const value = (assistant as any)[key];
        console.log(`  ${key}: ${typeof value} ${Array.isArray(value) ? '(array)' : ''} ${value === null ? '(null)' : ''} ${value === undefined ? '(undefined)' : ''}`);
      });

      // Use a batch write to ensure all documents are created atomically
      const batch = writeBatch(this.db);
      const assistantRef = doc(this.db, 'assistants', assistantId);

      batch.set(assistantRef, assistant);
      
      // Validate field types
      console.log('Validating field types...');
      if (typeof assistant.accessControl !== 'object' || !assistant.accessControl.type) {
        console.error('❌ Invalid accessControl:', assistant.accessControl);
        throw new Error('Invalid accessControl object');
      }
      
      if (!Array.isArray(assistant.tools)) {
        console.error('❌ tools is not an array:', assistant.tools);
        throw new Error('tools must be an array');
      }
      
      if (typeof assistant.toolConfigs !== 'object') {
        console.error('❌ toolConfigs is not an object:', assistant.toolConfigs);
        throw new Error('toolConfigs must be an object');
      }
      
      console.log('✅ All validations passed');
      
      // Check if user document exists before batching
      console.log('Checking user document...');
      const userRef = doc(this.db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        console.error('❌ User document does not exist! Creating one...');
        // Create a minimal user document first
        await setDoc(userRef, {
          userId: user.uid,
          email: user.email || '',
          displayName: user.displayName || user.email || 'User',
          photoURL: user.photoURL || null,
          createdAt: Date.now(),
          lastLogin: Date.now(),
          assistantCount: 0
        });
        console.log('✅ User document created');
      } else {
        console.log('✅ User document exists:', userDoc.data());
      }
      
      // Try operations separately to identify which one fails
      console.log('Attempting to create assistant document...');
      
      try {
        // First try to create the assistant document
        const assistantRef = doc(this.db, 'assistants', assistantId);
        await setDoc(assistantRef, assistant);
        console.log('✅ Assistant document created successfully');
        
        // Then update the user count
        console.log('Attempting to update user assistant count...');
        await updateDoc(userRef, {
          assistantCount: increment(1),
          updatedAt: Date.now()
        });
        console.log('✅ User assistant count updated successfully');
        
      } catch (specificError: any) {
        console.error('❌ Specific operation failed:', specificError.message);
        console.error('Failed operation details:', specificError);
        
        // Check if it's a validation error
        if (specificError.message?.includes('permission')) {
          console.log('Checking which Firestore rule might be failing...');
          
          // Log the exact fields being sent
          console.log('Assistant document fields:', Object.keys(assistant).sort());
          
          // Check against the required fields in Firestore rules
          const requiredInRules = ['assistantId', 'templateId', 'name', 'avatar', 'ownerId', 'ownerEmail',
                                  'initialMessage', 'initialInstructions', 'tools', 'toolConfigs',
                                  'createdAt', 'updatedAt', 'usageCount', 'lastAccessedAt', 'accessControl'];
          const optionalInRules = ['initialDocuments', 'tags', 'emailSubscriptions', 'parentAssistantId', 'derivationType', 'generationNumber'];
          const allAllowedFields = [...requiredInRules, ...optionalInRules];
          
          const extraFields = Object.keys(assistant).filter(field => !allAllowedFields.includes(field));
          if (extraFields.length > 0) {
            console.error('❌ Extra fields not allowed by Firestore rules:', extraFields);
          }
          
          const missingRequired = requiredInRules.filter(field => !(field in assistant));
          if (missingRequired.length > 0) {
            console.error('❌ Missing required fields per Firestore rules:', missingRequired);
          }
          
          // Additional validation checks
          console.log('Validation details:');
          console.log('- ownerId:', assistant.ownerId);
          console.log('- current user uid:', user.uid);
          console.log('- ownerId matches current user:', assistant.ownerId === user.uid);
          console.log('- ownerEmail:', assistant.ownerEmail);
          console.log('- current user email:', user.email);
          console.log('- ownerEmail matches current user:', assistant.ownerEmail === user.email);
          console.log('- accessControl type:', assistant.accessControl?.type);
          console.log('- tools is array:', Array.isArray(assistant.tools));
          console.log('- tools content:', assistant.tools);
          console.log('- toolConfigs is object:', typeof assistant.toolConfigs === 'object');
          console.log('- toolConfigs content:', assistant.toolConfigs);
          
          // Check specific field sizes that have limits in rules
          console.log('Field size checks:');
          console.log('- name length:', assistant.name?.length);
          console.log('- avatar length:', assistant.avatar?.length);
          console.log('- initialMessage length:', assistant.initialMessage?.length);
          console.log('- initialInstructions length:', assistant.initialInstructions?.length);
          console.log('- emailSubscriptions count:', assistant.emailSubscriptions?.length);
        }
        
        throw specificError;
      }

      console.log('✅ Assistant created successfully:', assistantId);

      // Handle provenance and first message copy (WAIT for completion if copying message)
      if (options?.parentAssistantId) {
        if (options.copyFirstMessage !== false) {
          // IMPORTANT: Wait for message copy to complete before returning
          // This ensures the greeting is in Firestore before page redirect
          await this.handleProvenanceAndFirstMessage(
            assistantId,
            options.parentAssistantId,
            true
          );
        } else {
          // If not copying message, can run in background
          this.handleProvenanceAndFirstMessage(
            assistantId,
            options.parentAssistantId,
            false
          ).catch(error => {
            console.warn('Non-critical: Failed to handle provenance:', error);
          });
        }
      }

      return assistantId;
    } catch (error: any) {
      console.error('❌ Firestore batch commit failed:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      this.handleError(error, 'Creating assistant');
      console.log(`createAssistant error: assistantData:`, JSON.stringify(assistantData, null, 2));
      throw error;
    }
  }

  /**
   * Handle provenance tracking and first message copying in background.
   * This is called after assistant creation and runs non-blocking.
   *
   * NOTE: initialMessage is already copied via createDuplicateConfig(),
   * so this function is mainly for future enhancements (e.g., creating
   * provenance collection records, generating AI greetings, etc.)
   */
  private static async handleProvenanceAndFirstMessage(
    newAssistantId: string,
    parentAssistantId: string,
    copyFirstMessage: boolean
  ): Promise<void> {
    try {
      console.log(`✅ Created assistant ${newAssistantId} from parent ${parentAssistantId}`);
      console.log(`📊 Provenance: derivationType=duplicate, copyFirstMessage=${copyFirstMessage}`);

      // Copy the first message from parent to child (including link to parent)
      if (copyFirstMessage) {
        console.log(`📝 Copying first message from ${parentAssistantId} to ${newAssistantId}...`);

        const response = await fetch('/api/proxy', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            endpoint: `/vac/assistants/${newAssistantId}/copy-first-message`,
            method: 'POST',
            parentAssistantId
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Failed to copy first message:', errorText);
          throw new Error(`Failed to copy first message: ${errorText}`);
        }

        const result = await response.json();
        console.log('✅ First message copied successfully:', result);

        if (!result.success) {
          throw new Error(`Backend reported failure: ${result.error || 'Unknown error'}`);
        }
      }
    } catch (error) {
      console.error('Error in handleProvenanceAndFirstMessage:', error);
      // Re-throw error when copying first message (critical for avoiding duplicate AI calls)
      if (copyFirstMessage) {
        throw error;
      }
      // Otherwise, silently fail (non-critical provenance tracking)
    }
  }

  static async getAssistant(assistantId: string): Promise<Assistant | null> {
    try {
      const assistantRef = doc(this.db, 'assistants', assistantId);
      const assistantSnap = await getDoc(assistantRef);
      
      return assistantSnap.exists() ? assistantSnap.data() as Assistant : null;
    } catch (error: any) {
      this.handleError(error, 'Getting assistant');
      throw error;
    }
  }
  
  static async getUserAssistants(userId?: string): Promise<Assistant[]> {
    try {
      const user = userId || this.auth.currentUser?.uid;
      if (!user) throw new Error('User not specified or not authenticated');
      
      const assistantsRef = collection(this.db, 'assistants');
      const q = query(assistantsRef, where('ownerId', '==', user), orderBy('createdAt', 'desc'));
      const assistantsSnap = await getDocs(q);
      
      return assistantsSnap.docs.map(doc => doc.data() as Assistant);
    } catch (error: any) {
      this.handleError(error, 'Getting user assistants');
      throw error;
    }
  }
  
  static async getPublicAssistants(): Promise<Assistant[]> {
    try {
      const assistantsRef = collection(this.db, 'assistants');
      const q = query(
        assistantsRef,
        where('accessControl.type', '==', 'public'),
        orderBy('createdAt', 'desc')
      );
      const assistantsSnap = await getDocs(q);
      
      return assistantsSnap.docs.map(doc => doc.data() as Assistant);
    } catch (error: any) {
      this.handleError(error, 'Getting public assistants');
      throw error;
    }
  }
  
  static async getSharedAssistantsByEmail(userEmail: string): Promise<Assistant[]> {
    try {
      const assistantsRef = collection(this.db, 'assistants');
      const q = query(
        assistantsRef,
        where('accessControl.type', '==', 'specific'),
        where('accessControl.emails', 'array-contains', userEmail),
        orderBy('createdAt', 'desc')
      );
      const assistantsSnap = await getDocs(q);
      
      return assistantsSnap.docs.map(doc => doc.data() as Assistant);
    } catch (error: any) {
      this.handleError(error, 'Getting shared assistants by email');
      throw error;
    }
  }
  
  static async getSharedAssistantsByDomain(userDomain: string): Promise<Assistant[]> {
    try {
      // First get domain-type assistants
      const domainAssistantsRef = collection(this.db, 'assistants');
      const domainQuery = query(
        domainAssistantsRef,
        where('accessControl.type', '==', 'domain'),
        where('accessControl.domain', '==', userDomain),
        orderBy('createdAt', 'desc')
      );
      const domainSnap = await getDocs(domainQuery);
      const domainAssistants = domainSnap.docs.map(doc => doc.data() as Assistant);
      
      // Then get domains-type assistants
      const multiDomainAssistantsRef = collection(this.db, 'assistants');
      const multiDomainQuery = query(
        multiDomainAssistantsRef,
        where('accessControl.type', '==', 'domains'),
        where('accessControl.domains', 'array-contains', userDomain),
        orderBy('createdAt', 'desc')
      );
      const multiDomainSnap = await getDocs(multiDomainQuery);
      const multiDomainAssistants = multiDomainSnap.docs.map(doc => doc.data() as Assistant);
      
      // Combine and remove duplicates
      return [
        ...domainAssistants,
        ...multiDomainAssistants.filter(a => 
          !domainAssistants.some(da => da.assistantId === a.assistantId)
        )
      ];
    } catch (error: any) {
      this.handleError(error, 'Getting shared assistants by domain');
      throw error;
    }
  }
  
  static async getAllAccessibleAssistants(): Promise<Assistant[]> {
    try {
      const user = this.auth.currentUser;
      if (!user || !user.email) throw new Error('User not authenticated');
      
      const userDomain = user.email.split('@')[1];
      
      // Run all queries in parallel
      const [ownedAssistants, publicAssistants, emailSharedAssistants, domainSharedAssistants] = await Promise.all([
        this.getUserAssistants(user.uid),
        this.getPublicAssistants(),
        this.getSharedAssistantsByEmail(user.email),
        userDomain ? this.getSharedAssistantsByDomain(userDomain) : Promise.resolve([])
      ]);
      
      // Combine all results, removing duplicates by assistantId
      const allAssistants: Assistant[] = [];
      const seenIds = new Set<string>();
      
      // Add in priority order: owned, email-shared, domain-shared, public
      for (const assistant of [...ownedAssistants, ...emailSharedAssistants, ...domainSharedAssistants, ...publicAssistants]) {
        if (!seenIds.has(assistant.assistantId)) {
          allAssistants.push(assistant);
          seenIds.add(assistant.assistantId);
        }
      }
      
      return allAssistants.sort((a, b) => b.createdAt - a.createdAt);
    } catch (error: any) {
      this.handleError(error, 'Getting all accessible assistants');
      throw error;
    }
  }
  
  static async updateAssistant(assistantId: string, updates: Partial<Assistant>): Promise<void> {
    try {
      const user = this.auth.currentUser;
      if (!user) throw new Error('User not authenticated');
      
      const assistantRef = doc(this.db, 'assistants', assistantId);
      const assistantSnap = await getDoc(assistantRef);
      
      if (!assistantSnap.exists()) {
        throw new Error('Assistant not found');
      }
      
      const assistant = assistantSnap.data() as Assistant;
      
      // Only allow the owner to update
      if (assistant.ownerId !== user.uid) {
        throw new Error('Not authorized to update this assistant');
      }
      
      await updateDoc(assistantRef, {
        ...updates,
        updatedAt: Date.now()
      });
    } catch (error: any) {
      this.handleError(error, 'Updating assistant');
      console.log(`error: assistantId: ${assistantId} - updates: ${JSON.stringify(updates)}`)
      throw error;
    }
  }
  
  static async deleteAssistant(assistantId: string): Promise<void> {
    try {
      const user = this.auth.currentUser;
      if (!user) throw new Error('User not authenticated');
      
      const assistantRef = doc(this.db, 'assistants', assistantId);
      const assistantSnap = await getDoc(assistantRef);
      
      if (!assistantSnap.exists()) {
        throw new Error('Assistant not found');
      }
      
      const assistant = assistantSnap.data() as Assistant;
      
      // Only allow the owner to delete
      if (assistant.ownerId !== user.uid) {
        throw new Error('Not authorized to delete this assistant');
      }
      
      // Use a batch write for atomic operations
      const batch = writeBatch(this.db);
      
      // Delete the assistant document
      batch.delete(assistantRef);
      
      // Update user's assistant count
      const userRef = doc(this.db, 'users', user.uid);
      batch.update(userRef, {
        assistantCount: increment(-1),
        updatedAt: Date.now()
      });
      
      // Commit the batch
      await batch.commit();
      
      // We need to delete messages separately since they're in a subcollection
      await this.deleteAllMessages(assistantId);
      
      console.log(`Assistant ${assistantId} successfully deleted`);
    } catch (error: any) {
      this.handleError(error, 'Deleting assistant');
      throw error;
    }
  }
  
  // Message Management
  static async getMessages(assistantId: string, messageLimit: number = 50): Promise<Message[]> {
    try {
      const messagesRef = collection(this.db, 'assistants', assistantId, 'messages');
      const q = query(
        messagesRef, 
        orderBy('timestamp', 'desc'),
        limit(messageLimit)
      );
      const messagesSnap = await getDocs(q);
      
      return messagesSnap.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data() as Message
        }))
        .sort((a, b) => a.timestamp - b.timestamp); // Re-sort in ascending order
    } catch (error: any) {
      this.handleError(error, 'Getting messages');
      throw error;
    }
  }
  
  static async addMessage(assistantId: string, message: Omit<Message, 'id' | 'timestamp'>): Promise<void> {
    try {
      console.log(`Attempting to add message to assistant ${assistantId}`, {
        messageData: JSON.stringify(message),
        sender: message.sender,
        userEmail: message.userEmail
      });
  
      // Check if we have an assistantId
      if (!assistantId) {
        throw new Error('No assistantId provided');
      }
  
      // Validate basic message structure
      if (!message.content) {
        throw new Error('Invalid message: missing any content to show');
      }
      if (!message.sender) {
        throw new Error('Invalid message: missing sender field');
      }
      if (!message.userEmail) {
        throw new Error('Invalid message: missing userEmail');
      }

      // Add this check to verify the assistant exists
      const assistantDoc = await getDoc(doc(this.db, 'assistants', assistantId));
      if (!assistantDoc.exists()) {
        console.error(`Assistant ${assistantId} not found`);
        throw new Error(`Assistant ${assistantId} not found`);
      }
  
      const timestamp = Date.now();
      
      const messageData: Omit<Message, 'id'> = {
        ...message,
        timestamp,
        read: message.sender === 'assistant' ? true : (message.read || false),
        userName: message.userName || (message.sender === 'assistant' ? 'Assistant' : 'User'),
      };
      
      // Add message to subcollection
      const messagesRef = collection(this.db, 'assistants', assistantId, 'messages');
      await addDoc(messagesRef, messageData);
      
    } catch (error: any) {
      console.error('Error adding message:', error);
      throw new Error(`Adding message failed: ${error.message}`);
    }
  }
  
  static onMessages(assistantId: string, callback: (messages: Message[]) => void): () => void {
    try {
      const messagesRef = collection(this.db, 'assistants', assistantId, 'messages');
      // Add orderBy and limit to the query
      const q = query(
        messagesRef, 
        orderBy('timestamp', 'desc'), // Get newest messages first
        limit(50) // Limit to 50 messages
      );
      
      return onSnapshot(q, (snapshot) => {
        const messages = snapshot.docs
          .map(doc => {
            const data = doc.data();
            // Debug log for structured output
            if (data.structuredOutput) {
              console.log('FirebaseService.onMessages: Found message with structuredOutput:', {
                messageId: doc.id,
                sender: data.sender,
                hasStructuredOutput: true,
                structuredOutputFields: Object.keys(data.structuredOutput)
              });
            }
            return {
              id: doc.id,
              ...data as Message
            };
          })
          .sort((a, b) => a.timestamp - b.timestamp); // Re-sort in ascending order
        
        callback(messages);
      }, (error) => {
        console.error('Error in message listener:', error);
        callback([]);
      });
    } catch (error) {
      console.error('Error setting up message listener:', error);
      return () => {};
    }
  }
  
  
  
  static async markMessagesAsRead(assistantId: string, userEmail: string): Promise<void> {
    try {
      const messagesRef = collection(this.db, 'assistants', assistantId, 'messages');
      const q = query(
        messagesRef,
        where('userEmail', '!=', userEmail),
        where('read', '==', false)
      );
      
      const snapshot = await getDocs(q);
      
      // If no unread messages, return early
      if (snapshot.empty) return;
      
      // Use individual updates for better error handling
      const updatePromises = snapshot.docs.map(async doc => {
        try {
          await updateDoc(doc.ref, { read: true });
        } catch (error) {
          console.warn(`Failed to mark message ${doc.id} as read:`, error);
          // Don't throw for individual failures
        }
      });
      
      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error marking messages as read:', error);
      // Don't throw, this is non-critical
    }
  }
  
  static async deleteMessage(assistantId: string, messageId: string): Promise<void> {
    try {
      const user = this.auth.currentUser;
      if (!user) throw new Error('User not authenticated');
      
      // Get the assistant to check ownership
      const assistantRef = doc(this.db, 'assistants', assistantId);
      const assistantSnap = await getDoc(assistantRef);
      
      if (!assistantSnap.exists()) {
        throw new Error('Assistant not found');
      }
      
      const assistant = assistantSnap.data() as Assistant;
      
      // Only allow the owner to delete messages
      if (assistant.ownerId !== user.uid) {
        throw new Error('Not authorized to delete messages from this assistant');
      }
      
      const messageRef = doc(this.db, 'assistants', assistantId, 'messages', messageId);
      await deleteDoc(messageRef);
    } catch (error: any) {
      this.handleError(error, 'Deleting message');
      throw error;
    }
  }
  
  // Helper Methods
  private static async deleteAllMessages(assistantId: string): Promise<void> {
    try {
      const messagesRef = collection(this.db, 'assistants', assistantId, 'messages');
      const messagesSnap = await getDocs(messagesRef);
      
      // If no messages, return early
      if (messagesSnap.empty) return;
      
      // Delete messages in batches of 500 (Firestore limit)
      const batchSize = 500;
      const batches = [];
      let currentBatch = writeBatch(this.db);
      let operationCount = 0;
      
      messagesSnap.docs.forEach(doc => {
        currentBatch.delete(doc.ref);
        operationCount++;
        
        if (operationCount === batchSize) {
          batches.push(currentBatch.commit());
          currentBatch = writeBatch(this.db);
          operationCount = 0;
        }
      });
      
      // Commit any remaining operations
      if (operationCount > 0) {
        batches.push(currentBatch.commit());
      }
      
      await Promise.all(batches);
    } catch (error) {
      console.error('Error deleting messages:', error);
      // Don't throw, as we've already deleted the main document
    }
  }
  
  static async checkAssistantAccess(assistantId: string): Promise<boolean> {
    try {
      const user = this.auth.currentUser;
      if (!user || !user.email) return false;
      
      const assistantRef = doc(this.db, 'assistants', assistantId);
      const assistantSnap = await getDoc(assistantRef);
      
      if (!assistantSnap.exists()) return false;
      
      const assistant = assistantSnap.data() as Assistant;
      
      // Owner always has access
      if (assistant.ownerId === user.uid) return true;
      
      // Check access control settings
      const { accessControl } = assistant;
      
      switch (accessControl.type) {
        case 'public':
          return true;
          
        case 'private':
          return false; // Only owner has access, already checked above
          
        case 'specific':
          return Array.isArray(accessControl.emails) && 
                 accessControl.emails.includes(user.email);
          
        case 'domain': {
          const userDomain = user.email.split('@')[1];
          return accessControl.domain === userDomain;
        }
          
        case 'domains': {
          const userDomain = user.email.split('@')[1];
          return Array.isArray(accessControl.domains) && 
                 accessControl.domains.includes(userDomain);
        }

        case 'group': {
          // Check if user is a member of any specified group
          if (!Array.isArray(accessControl.groups) || accessControl.groups.length === 0) {
            return false;
          }
          
          // If user has no email or groups is empty, deny access
          if (!user.email) return false;
          
          const userEmail = user.email; // Create a local constant that TypeScript knows is not null
          
          return accessControl.groups.some(groupId => {
            // Get the predefined groups that match the group ID
            const group = predefinedGroups.find(g => g.id === groupId);
            // Check if the user's email is in the group's emails
            return group ? group.emails.includes(userEmail) : false;
          });
        }
          
        default:
          return false;
      }
    } catch (error) {
      console.error('Error checking assistant access:', error);
      return false;
    }
  }
  
  // Usage and Analytics
  static async incrementAssistantUsage(assistantId: string): Promise<void> {
    try {
      const assistantRef = doc(this.db, 'assistants', assistantId);
      const currentTime = Date.now(); // Use numeric timestamp for consistency with other timestamps
      
      console.log(`Updating lastAccessedAt for assistant ${assistantId} with numeric timestamp: ${currentTime}`);
      
      await updateDoc(assistantRef, {
        usageCount: increment(1),
        lastAccessedAt: currentTime // Use numeric timestamp instead of serverTimestamp()
      });
    } catch (error) {
      console.warn('Error incrementing assistant usage:', error);
      // Don't throw, this is non-critical
    }
  }
  
  // Utility methods
  static formatTimestamp(timestamp: number): string {
    return new Date(timestamp).toLocaleString();
  }
  
  static async createPublicWelcomeAssistant(): Promise<string | null> {
    if (typeof window === 'undefined') return null;
    
    try {
      // First, check if welcome assistant already exists
      const assistantsRef = collection(this.db, 'assistants');
      const q = query(assistantsRef, where('name', '==', 'Welcome to Aitana'));
      const existingSnap = await getDocs(q);
      
      if (!existingSnap.empty) {
        // Already exists, return its ID
        return existingSnap.docs[0].id;
      }
      
      const user = this.auth.currentUser;
      // Only admin can create the welcome assistant
      if (!user || user.email !== process.env.NEXT_PUBLIC_ADMIN_EMAIL) {
        console.log('Only admin can create the welcome assistant');
        return null;
      }
      
      // Find a general template to use
      const templatesRef = collection(this.db, 'templates');
      const templateQuery = query(templatesRef, where('isTemplate', '==', true));
      const templatesSnap = await getDocs(templateQuery);
      
      if (templatesSnap.empty) {
        console.warn('No templates found to create welcome assistant');
        return null;
      }
      
      // Use the first template
      const template = templatesSnap.docs[0].data() as Template;
      
      // Create the welcome assistant
      const welcomeAssistant: Omit<Assistant, 'assistantId' | 'createdAt' | 'updatedAt' | 'usageCount' | 'lastAccessedAt'> = {
        templateId: template.templateId,
        name: 'Welcome to Aitana',
        avatar: '/images/avatars/aitana_help.jpeg',
        ownerId: user.uid,
        ownerEmail: user.email,
        initialMessage: 'Welcome to Aitana! I\'m here to help you get started. Feel free to ask me any questions about how to use the platform.',
        initialInstructions: 'You are an helpful assistant that explains how to use the Aitana platform.',
        initialDocuments: [],
        tools: ['preview','highlights','plots','alerts','tooltips','file-browser','google_search_retrieval','vertex_search'],
        toolConfigs: {'file-browser':{'bucketUrl':'aitana-public-bucket'},'google_search_retrieval':{},'vertex_search':{'datastore_id':'aitana_public_welcome'}},
        accessControl: {
          type: 'public'
        },
        emailSubscriptions: [] // Ensure emailSubscriptions is always set
      };
      
      // Create the assistant
      const assistantId = await this.createAssistant(welcomeAssistant);
      
      // Add initial welcome message
      await this.addMessage(assistantId, {
        content: welcomeAssistant.initialMessage,
        userEmail: user.email,
        userName: 'Aitana Team',
        sender: 'assistant',
        read: true
      });
      
      console.log('Welcome assistant created successfully');
      return assistantId;
    } catch (error) {
      console.error('Error creating welcome assistant:', error);
      return null;
    }
  }

  // Tag Management Methods
  static async createTag(tag: Omit<Tag, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const tagsRef = collection(this.db, 'tags');
      const now = Date.now();
      const newId = doc(tagsRef).id; // Generate ID before creating
      
      // Filter out undefined values to prevent Firestore errors
      const cleanTag = Object.fromEntries(
        Object.entries(tag).filter(([_, value]) => value !== undefined)
      );
      
      const tagData: Tag = {
        ...cleanTag,
        id: newId,
        createdAt: now,
        updatedAt: now
      } as Tag;
      
      // Use setDoc with the pre-generated ID instead of addDoc
      const docRef = doc(tagsRef, newId);
      await setDoc(docRef, tagData);
      
      return newId;
    } catch (error) {
      console.error('Error creating tag:', error);
      throw error;
    }
  }

  static async updateTag(tagId: string, updates: Partial<Omit<Tag, 'id' | 'createdAt'>>): Promise<void> {
    try {
      const tagRef = doc(this.db, 'tags', tagId);
      
      // Filter out undefined values to prevent Firestore errors
      const cleanUpdates = Object.fromEntries(
        Object.entries(updates).filter(([_, value]) => value !== undefined)
      );
      
      await updateDoc(tagRef, {
        ...cleanUpdates,
        updatedAt: Date.now()
      });
    } catch (error) {
      console.error('Error updating tag:', error);
      throw error;
    }
  }

  static async deleteTag(tagId: string): Promise<void> {
    try {
      const tagRef = doc(this.db, 'tags', tagId);
      await deleteDoc(tagRef);
    } catch (error) {
      console.error('Error deleting tag:', error);
      throw error;
    }
  }

  static async getAllTags(): Promise<Tag[]> {
    try {
      const tagsRef = collection(this.db, 'tags');
      const snapshot = await getDocs(tagsRef);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Tag));
    } catch (error) {
      console.error('Error fetching tags:', error);
      throw error;
    }
  }

  static async getTag(tagId: string): Promise<Tag | null> {
    try {
      const tagRef = doc(this.db, 'tags', tagId);
      const snapshot = await getDoc(tagRef);
      
      if (!snapshot.exists()) {
        return null;
      }
      
      return {
        id: snapshot.id,
        ...snapshot.data()
      } as Tag;
    } catch (error) {
      console.error('Error fetching tag:', error);
      throw error;
    }
  }

  // Initialize admin-tools tag if it doesn't exist
  static async initializeAdminToolsTag(adminEmail: string): Promise<void> {
    try {
      // Check if admin-tools tag already exists
      const tagsRef = collection(this.db, 'tags');
      const q = query(tagsRef, where('name', '==', 'admin-tools'));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        // Create the admin-tools tag
        await this.createTag({
          name: 'admin-tools',
          description: 'Administrative tools and features restricted to specific users',
          accessControl: {
            type: 'specific',
            emails: [adminEmail]
          },
          createdBy: adminEmail
        });
        console.log('Created admin-tools tag for', adminEmail);
      } else {
        console.log('admin-tools tag already exists');
      }
    } catch (error) {
      console.error('Error initializing admin-tools tag:', error);
      throw error;
    }
  }
}

export default FirebaseService;